import CartItem from '@/classes/CartItem';
import { Module } from 'vuex';
import { CartAPI } from '@/api';
import Product from '@/classes/Product';
import { setCookie } from '@/helpers';
import CampaignInterface from '@/interfaces/CampaignInterface';

const cartModule: Module<any, any> = {
  namespaced: true,

  state: {
    loading: false,
    items: {} as Record<string, CartItem[]>,
    campaigns: [] as CampaignInterface[],
    error: null,
    showWidget: false,
  },

  mutations: {
    SET_LOADING(state, isLoading: boolean) {
      state.loading = isLoading;
    },
    SET_ITEMS(state, items: Record<string, CartItem[]>) {
      state.items = items;
    },
    SET_CAMPAIGNS(state, cartCampaigns: CampaignInterface[]) {
      state.campaigns = cartCampaigns;
    },
    SET_ERROR(state, error: unknown) {
      state.error = error;
    },
    TOGGLE_SHOW_WIDGET(state) {
      state.showWidget = !state.showWidget;
    },
    SHOW_WIDGET(state) {
      state.showWidget = true;
    },
  },

  actions: {
    fetch({ commit }) {
      commit('SET_LOADING', true);

      CartAPI.get()
        .then((response) => {
          commit('SET_ERROR', null);
          commit('SET_ITEMS', response || {});
          commit('SET_CAMPAIGNS', response?.campaigns || []);
        }).catch((e) => {
          commit('SET_ERROR', e);
          commit('SET_ITEMS', {});
          commit('SET_CAMPAIGNS', []);
        }).finally(() => {
          commit('SET_LOADING', false);
        });
    },
    add({ commit }, data: {
      product: Product
      cartItemKey: string | null
      params: Record<string, any>
      additionalData: Record<string, any>
      entry: boolean
    }): Promise<unknown> {
      commit('SET_LOADING', true);

      return new Promise((resolve, reject) => {
        CartAPI.add(data.product, data.params, data.cartItemKey, data.additionalData).then((response) => {
          if (response.cartKey) {
            setCookie('io_shop_cart', response.cartKey);
          }

          commit('SET_ERROR', null);
          commit('SET_ITEMS', response?.cartData || {});
          commit('SET_CAMPAIGNS', response?.cartData?.campaigns || []);

          resolve(response);
        }).catch((e) => {
          commit('SET_ERROR', e);
          reject(e);
        }).finally(() => {
          commit('SET_LOADING', false);
        });
      });
    },
    remove({ commit }, payload: { key: string, product: Product }): Promise<unknown> {
      const {
        product,
        key,
      } = payload;

      commit('SET_LOADING', true);

      return new Promise((resolve, reject) => {
        CartAPI.remove(key, product).then((response) => {
          if (response.cartKey) {
            setCookie('io_shop_cart', response.cartKey);
          }

          commit('SET_ERROR', null);
          commit('SET_ITEMS', response?.cartData || {});
          commit('SET_CAMPAIGNS', response?.cartData?.campaigns || []);

          resolve(response);
        }).catch((e) => {
          commit('SET_ERROR', e);
          reject(e);
        }).finally(() => {
          commit('SET_LOADING', false);
        });
      });
    },
  },

  getters: {
    status: (state) => (type = 'commodity') => {
      const status = state.status[type];

      return typeof status === 'undefined' ? 0 : status;
    },
    isLoading: (state) => state.loading,
    showWidget: (state) => state.showWidget,
    all: (state) => Object.keys(state.items).map((category) => state.items[category] || []).flat(),
    get: (state) => (category: string) => state.items[category] || [],
    commodity: (state, getters) => getters.get('commodity'),
    commodityByBranches: (state, getters): Record<string, CartItem[]> => {
      const commodityByBranches = {} as Record<string, CartItem[]>;

      getters.commodity.forEach((cartCommodityItem: CartItem) => {
        if (commodityByBranches[cartCommodityItem.product.branch] === undefined) {
          commodityByBranches[cartCommodityItem.product.branch] = [];
        }

        commodityByBranches[cartCommodityItem.product.branch].push(cartCommodityItem);
      });
      return commodityByBranches;
    },
    commodityBranches: (state, getters) => Object.keys(getters.commodityByBranches),
    isCommodityMultipleBranches: (state, getters) => getters.commodityBranches.length >= 2,
    commodityFirst: (state) => (state.commodity.length ? state.items.commodity?.[0] : null),
    count: (state) => (category: string): number => state.items[category]?.length || 0,
    countCommodity: (state, getters) => getters.count('commodity'),
    countAll: (state, getters) => getters.all.length,
    perMonth(state, getters) {
      let perMonth = 0;

      getters.all.forEach((cartCommodityItem: CartItem) => {
        if (cartCommodityItem.attributes.product.isInternet) {
          perMonth += cartCommodityItem.product.getRecurringPriceWithSavings('base_price', true);
        } else {
          perMonth += cartCommodityItem.product.getPriceValue('monthly', 'gross');
        }
      });

      return perMonth;
    },
    oneTime(state, getters) {
      let oneTime = 0;

      getters.all.forEach((cartCommidityItem: CartItem) => {
        if (cartCommidityItem.attributes.product.isInternet) {
          oneTime += cartCommidityItem.product.getOneTimePriceValue(['setup_fee', 'base_price'], true);
        }
      });

      return oneTime;
    },
    commodityPerYear(state, getters) {
      let total = 0;

      getters.commodity.forEach((cartCommotidyItem: CartItem) => {
        total += cartCommotidyItem.product.getPriceValue('yearly', 'gross');
      });

      return total;
    },
  },
};

export default cartModule;
